/**
 * AffiliateReferrals — the signups behind the affiliate's dashboard totals.
 *
 * A signup no longer credits the affiliate on registration: it lands pending
 * and becomes payable once the referred member clears the active qualification
 * rule. This screen is where that is visible per signup, which is what makes
 * the lower Earned figure on the dashboard explicable rather than alarming.
 *
 * The API masks the referred member for an affiliate's own view — no email, no
 * photo, no registration IP, a single-letter name — so every row is laid out
 * for initials and a placeholder avatar. Nothing here tries to recover an
 * identity the API withheld.
 *
 * Progress is rendered from the metric/threshold pairs on each row and the
 * server's own `criteriaMetMask`. There is no client-side rule evaluation: the
 * referral was graded under `appliedRuleId` and the thresholds beside it belong
 * to the rule in force now, so re-deriving pass/fail would disagree with the
 * API as soon as the rule changes.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import affiliateService from '@wlb/shared/api/affiliate/affiliateService';
import { AFFILIATE_QUALIFICATION_STATUSES } from '@wlb/shared/api/affiliate/affiliateQualification';
import type {
  AffiliateQualificationStatus,
  AffiliateReferral,
  AffiliateReferralPage,
} from '@wlb/shared/api/affiliate/affiliateTypes';
import { useAffiliateSession } from '@wlb/shared/context/AffiliateSessionContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import {
  affiliateCriterionProgressLabel,
  requiredAffiliateCriteria,
} from '@wlb/shared/utils/affiliateReferralProgress';
import { Button } from '../components/ui/Button';
import { ListStatus } from '../components/ui/ListStatus';
import { Skeleton, SkeletonGroup, skeletonKeys } from '../components/ui/Skeleton';
import { ChevronLeftIcon, ChevronRightIcon, RefreshIcon } from '../components/ui/icons';
import {
  affiliateReferralInitials,
  formatAffiliateCount,
  formatAffiliateCurrency,
  formatAffiliateDate,
} from '../features/affiliate/affiliateFormat';
import { AffiliatePortalLayout } from '../features/affiliate/AffiliatePortalLayout';
import { AFFILIATE_ROUTES } from '../features/affiliate/affiliateRoutes';
// The filter control reuses the `auth-flow__*` field primitives, so the file
// defining them is imported here rather than relied on via the layout.
import '../components/layout/authFlow.css';
import '../features/affiliate/affiliatePortal.css';

const PAGE_SIZE = 20;

/** i18n group the shared progress labels resolve against on this surface. */
const PROGRESS_KEYS = 'affiliate.referrals';

/** Pill tone per qualification status; the label carries the meaning too. */
const STATUS_TONES: Record<AffiliateQualificationStatus, string> = {
  pending: 'pending',
  qualified: 'eligible',
  adminApproved: 'eligible',
  rejected: 'rejected',
  reversed: 'rejected',
};

export function AffiliateReferrals() {
  const { t } = useTranslation();
  const { session, auth } = useAffiliateSession();
  const affiliateUserId = session?.affiliateUserId ?? 0;
  const [searchParams] = useSearchParams();

  // Arrives from the dashboard's per-code Referrals link. Parsed rather than
  // trusted: a hand-edited query must narrow to nothing or to one code, never
  // send `NaN` into the request body where it would bind as 0 and widen the
  // list back to every code.
  const codeFilter = useMemo(() => {
    const raw = Number(searchParams.get('code'));
    return Number.isInteger(raw) && raw > 0 ? raw : null;
  }, [searchParams]);

  const [status, setStatus] = useState<'' | AffiliateQualificationStatus>('');
  const [pageNumber, setPageNumber] = useState(1);
  const [page, setPage] = useState<AffiliateReferralPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!affiliateUserId) return;
    setLoading(true);
    setError('');
    try {
      const result = await affiliateService.getReferrals({
        auth,
        // Advisory only: the API pins the request to the signed-in affiliate,
        // so this cannot be used to read someone else's referrals.
        affiliateUserId,
        affiliateReferralCodeId: codeFilter,
        status: status === '' ? null : status,
        pageNumber,
        pageSize: PAGE_SIZE,
      });
      setPage(result);
    } catch (caught) {
      setError(getUserFacingErrorMessage(caught, t('affiliate.referrals.loadError')));
    } finally {
      setLoading(false);
    }
  }, [affiliateUserId, auth, codeFilter, pageNumber, status, t]);

  useEffect(() => {
    load();
  }, [load]);

  const referrals = page?.items ?? [];
  // An unfiltered empty list means the affiliate has no signups yet; a filtered
  // one means these filters match none. Different problems, different copy.
  const isFiltered = status !== '' || codeFilter !== null;

  const tiles: readonly { key: string; label: string; value: string }[] = [
    {
      key: 'total',
      label: t('affiliate.referrals.tiles.total'),
      value: formatAffiliateCount(page?.totalCount ?? 0),
    },
    {
      key: 'eligible',
      label: t('affiliate.referrals.tiles.eligible'),
      value: formatAffiliateCount(page?.eligibleCount ?? 0),
    },
    {
      key: 'pending',
      label: t('affiliate.referrals.tiles.pending'),
      value: formatAffiliateCount(page?.pendingCount ?? 0),
    },
    {
      key: 'rejected',
      label: t('affiliate.referrals.tiles.rejected'),
      value: formatAffiliateCount(page?.rejectedCount ?? 0),
    },
    {
      key: 'reversed',
      label: t('affiliate.referrals.tiles.reversed'),
      value: formatAffiliateCount(page?.reversedCount ?? 0),
    },
    {
      key: 'eligibleEarnings',
      label: t('affiliate.referrals.tiles.eligibleEarnings'),
      value: formatAffiliateCurrency(page?.eligibleEarnings ?? 0),
    },
    {
      key: 'pendingEarnings',
      label: t('affiliate.referrals.tiles.pendingEarnings'),
      value: formatAffiliateCurrency(page?.pendingEarnings ?? 0),
    },
  ];

  const renderMember = (referral: AffiliateReferral) => {
    const initials = affiliateReferralInitials(referral);
    return (
      <div className="affiliate-referral__member">
        {/* No image: the API withholds the photo from this view, so the circle
            is a placeholder and is hidden from assistive tech — the name
            beside it already identifies the row. */}
        <span className="affiliate-referral__avatar" aria-hidden>
          {initials}
        </span>
        <span className="affiliate-referral__member-name">
          {referral.userName || t('common.notProvided')}
        </span>
      </div>
    );
  };

  /**
   * The qualification date that actually applies to this referral.
   *
   * `eligibleFromDate` is a forecast of the earliest the tenure gate can clear,
   * so it only means anything while the referral is still being graded. Once it
   * is payable the useful date is when it qualified, and once it has been
   * refused no date applies at all — rendering the forecast on a rejected row
   * would read as a signup still on its way to earning.
   */
  const renderQualificationDate = (referral: AffiliateReferral) => {
    if (referral.status === 'rejected' || referral.status === 'reversed') {
      return t('affiliate.referrals.noQualificationDate');
    }
    const date = referral.isEligible
      ? formatAffiliateDate(referral.qualifiedAt) ||
        formatAffiliateDate(referral.eligibleFromDate)
      : formatAffiliateDate(referral.eligibleFromDate);
    return date || t('common.notProvided');
  };

  const renderProgress = (referral: AffiliateReferral) => {
    // A referral the API already calls payable must not be shown a checklist of
    // gates it failed. That is the normal state for the two cases the mask
    // cannot express: a signup grandfathered in before qualification existed
    // was never graded, and an operator override can approve one against the
    // mask. Listing unmet gates beside "Payable" would read as a system that
    // contradicts itself and invite a support ticket about money already owed.
    if (referral.isEligible) {
      return (
        <span className="affiliate-referral__qualified">
          {t('affiliate.referrals.qualifiedNote')}
        </span>
      );
    }
    const required = requiredAffiliateCriteria(referral.criteria);
    if (required.length === 0) {
      return <span>{t('affiliate.referrals.notRequired')}</span>;
    }
    return (
      <ul
        className="affiliate-referral__criteria"
        aria-label={t('affiliate.accessibility.referralCriteria')}
      >
        {required.map(criterion => (
          <li
            key={criterion.id}
            className={`affiliate-referral__criterion affiliate-referral__criterion--${
              criterion.met ? 'met' : 'unmet'
            }`}
          >
            <span className="affiliate-referral__criterion-label">
              {t(`affiliate.referrals.criteria.${criterion.id}`)}
            </span>
            <span className="affiliate-referral__criterion-value">
              {affiliateCriterionProgressLabel(criterion, t, PROGRESS_KEYS)}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <AffiliatePortalLayout
      headerActions={
        <Link
          className="affiliate-portal__header-link"
          to={AFFILIATE_ROUTES.DASHBOARD}
          aria-label={t('affiliate.accessibility.backToDashboard')}
        >
          <ChevronLeftIcon size={18} aria-hidden />
          {t('affiliate.dashboard.title')}
        </Link>
      }
    >
      <div className="affiliate-dashboard__header">
        <div>
          <h1 className="affiliate-dashboard__title">
            {t('affiliate.referrals.title')}
          </h1>
          <p className="affiliate-dashboard__subtitle">
            {t('affiliate.referrals.subtitle')}
          </p>
        </div>
      </div>

      <section className="affiliate-earnings">
        <fieldset className="affiliate-earnings__filters">
          <legend className="sr-only">{t('affiliate.referrals.title')}</legend>
          <label className="auth-flow__field affiliate-earnings__field">
            <span>{t('affiliate.referrals.statusFilterLabel')}</span>
            <select
              className="affiliate-auth__select"
              value={status}
              onChange={event => {
                setPageNumber(1);
                setStatus(event.target.value as '' | AffiliateQualificationStatus);
              }}
            >
              <option value="">{t('affiliate.referrals.statusFilterAll')}</option>
              {AFFILIATE_QUALIFICATION_STATUSES.map(option => (
                <option key={option} value={option}>
                  {t(`affiliate.referrals.status.${option}`)}
                </option>
              ))}
            </select>
          </label>
        </fieldset>

        <dl className="affiliate-earnings__tiles">
          {tiles.map(tile => (
            <div className="affiliate-earnings__tile" key={tile.key}>
              <dt className="affiliate-earnings__tile-label">{tile.label}</dt>
              <dd className="affiliate-earnings__tile-value">
                {loading ? (
                  <Skeleton className="affiliate-earnings__tile-skeleton" />
                ) : (
                  tile.value
                )}
              </dd>
            </div>
          ))}
        </dl>

        <p className="affiliate-earnings__note">
          {t('affiliate.referrals.pendingNote')}
        </p>
      </section>

      <section className="affiliate-dashboard__panel">
        <div className="affiliate-dashboard__panel-header">
          <h2 className="affiliate-dashboard__panel-title">
            {t('affiliate.referrals.tableCaption')}
          </h2>
          <div className="affiliate-dashboard__actions">
            <Button
              variant="ghost"
              size="sm"
              onClick={load}
              disabled={loading}
              aria-label={t('affiliate.referrals.refresh')}
              title={t('affiliate.referrals.refresh')}
            >
              <RefreshIcon size={16} />
            </Button>
          </div>
        </div>

        {loading && (
          <SkeletonGroup className="affiliate-dashboard__skeleton">
            {skeletonKeys(5).map(key => (
              <Skeleton key={key} className="affiliate-dashboard__skeleton-row" />
            ))}
          </SkeletonGroup>
        )}

        {!loading && error && (
          <ListStatus variant="error" message={error} onAction={load} />
        )}

        {!loading && !error && referrals.length === 0 && (
          <ListStatus
            variant="empty"
            title={t(
              isFiltered
                ? 'affiliate.referrals.filteredEmptyTitle'
                : 'affiliate.referrals.emptyTitle',
            )}
            message={t(
              isFiltered
                ? 'affiliate.referrals.filteredEmptyMessage'
                : 'affiliate.referrals.emptyMessage',
            )}
          />
        )}

        {!loading && !error && referrals.length > 0 && (
          <>
            <div className="affiliate-dashboard__table-scroll">
              <table className="affiliate-table">
                <caption className="sr-only">
                  {t('affiliate.referrals.tableCaption')}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">{t('affiliate.referrals.memberColumn')}</th>
                    <th scope="col">{t('affiliate.referrals.codeColumn')}</th>
                    <th scope="col">{t('affiliate.referrals.registeredColumn')}</th>
                    <th scope="col">{t('affiliate.referrals.statusColumn')}</th>
                    <th scope="col">{t('affiliate.referrals.progressColumn')}</th>
                    <th scope="col">
                      {t('affiliate.referrals.qualifiesOnColumn')}
                    </th>
                    <th scope="col" className="affiliate-table__numeric">
                      {t('affiliate.referrals.commissionColumn')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map(referral => (
                    <tr key={referral.id}>
                      <td>{renderMember(referral)}</td>
                      <td className="affiliate-table__code">
                        {referral.referralCodeUsed ||
                          referral.referralCode ||
                          t('common.notProvided')}
                      </td>
                      <td>
                        {formatAffiliateDate(referral.registeredAt) ||
                          t('common.notProvided')}
                      </td>
                      <td>
                        <span
                          className={`affiliate-status-pill affiliate-status-pill--${
                            STATUS_TONES[referral.status]
                          }`}
                        >
                          {t(`affiliate.referrals.status.${referral.status}`)}
                        </span>
                        {/* `isEligible` is the API's own verdict on whether this
                            row is payable, so it is shown rather than inferred
                            from the status above. */}
                        {referral.isEligible && (
                          <span className="affiliate-referral__payable">
                            {t('affiliate.referrals.eligibleBadge')}
                          </span>
                        )}
                      </td>
                      <td>{renderProgress(referral)}</td>
                      <td>{renderQualificationDate(referral)}</td>
                      <td className="affiliate-table__numeric">
                        {formatAffiliateCurrency(referral.commissionAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {page && page.totalPages > 1 && (
              <div className="affiliate-referrals__pagination">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!page.hasPreviousPage}
                  onClick={() => setPageNumber(current => Math.max(1, current - 1))}
                  aria-label={t('affiliate.referrals.previousPage')}
                >
                  <ChevronLeftIcon size={16} aria-hidden />
                </Button>
                <span className="affiliate-referrals__pagination-status">
                  {t('affiliate.referrals.pageStatus', {
                    page: page.pageNumber,
                    totalPages: page.totalPages,
                    count: formatAffiliateCount(page.totalCount),
                  })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!page.hasNextPage}
                  onClick={() => setPageNumber(current => current + 1)}
                  aria-label={t('affiliate.referrals.nextPage')}
                >
                  <ChevronRightIcon size={16} aria-hidden />
                </Button>
              </div>
            )}
          </>
        )}

        {/* Claimed from the rows that arrived, not from the assumption that an
            affiliate's own view is always masked: if the API ever stops
            withholding a field, the promise stops being made with it. */}
        {referrals.some(referral => referral.isIdentityMasked) ? (
          <p className="affiliate-earnings__note">
            {t('affiliate.referrals.maskedNote')}
          </p>
        ) : null}
      </section>
    </AffiliatePortalLayout>
  );
}
